import React, { createContext, ReactNode, useContext, useState } from 'react';

interface User {
  id: string;
  matricula?: string;
  nombre: string;
  apellido: string;
  doc?: string;
  email?: string;
  roles: number[];
  estado?: boolean;
  active?: boolean;
  fact_ioma?: boolean;
};

interface ContextData {
  getUser: () => User | null;
  setUser: (user: User | null) => void;
  getId: () => string | null;
  getDoc: () => string | null;
  getMatricula: () => string | null;
  getNombreApellido: () => string | null;
  is: (query: number | number[]) => boolean;
  isNot: (query: number | number[]) => boolean;
  data: User | null;
}

export const UserContext = createContext<ContextData | undefined>(undefined);

interface ProviderProps {
  children: ReactNode;
}

export default function Provider({ children }: ProviderProps) {
  const [userData, setUserData] = useState<User | null>(null);

  const getUser = () => {
    return userData;
  };

  const setUser = (user: User | null) => {
    setUserData(user);
  };

  const getId = () => {
    return userData ? userData.id : null;
  };

  const getDoc = () => {
    return userData && userData.doc ? userData.doc : null;
  };

  const getMatricula = () => {
    return userData && userData.matricula ? userData.matricula : null;
  };

  const getNombreApellido = () => {
    return userData ? `${userData.nombre} ${userData.apellido}` : null;
  };

  const is = (query: number | number[]) => {
    // Si no hay usuario, por defecto falso
    if (!userData) {
      return false;
    }

    // Si hay algún problema con la información de los roles, por defecto falso
    if (!userData.roles || !Array.isArray(userData.roles) || !userData.roles.length) {
      return false;
    }

    // Para simplificar el tratamiento nos aseguramos que la query siempre sea un arreglo de ids de roles
    if (!Array.isArray(query)) {
      query = [query];
    }

    // Devolvemos true o false dependiendo de si alguno de los roles pasados en la query está incluido en
    // los roles del usuario
    return query.some(roleId => userData.roles.includes(roleId));
  };

  const isNot = (query: number | number[]) => !is(query);

  const value = {
    getUser,
    setUser,
    getId,
    getMatricula,
    getDoc,
    getNombreApellido,
    is,
    isNot,
    data: userData,
  };

  return (
    <UserContext.Provider value={ value }>
      { children }
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);

  if (!context) {
    throw new Error('useUser must be used within the User Provider');
  }

  return context;
};
